import React from 'react';
import type { DailyNoteRecord, EventRecord, TaskRecord } from '../api';
import { DailyNoteEditor } from './DailyNoteEditor';
import { describeDate, formatDateKey, formatDisplayDate } from '../utils/date';

interface Props {
  tasks: TaskRecord[];
  events: EventRecord[];
  todayNote: DailyNoteRecord | null;
  onSaveNote: (date: string, updates: { title: string; content: string }) => Promise<void>;
  onSelectTask: (task: TaskRecord) => void;
  onSelectEvent: (event: EventRecord) => void;
  onOpenNotes: () => void;
}

type ScheduledItem =
  | { kind: 'task'; id: string; title: string; timeLabel: string; task: TaskRecord }
  | { kind: 'event'; id: string; title: string; timeLabel: string; event: EventRecord };

export function TodayView({
  tasks,
  events,
  todayNote,
  onSaveNote,
  onSelectTask,
  onSelectEvent,
  onOpenNotes,
}: Props) {
  const todayKey = formatDateKey(new Date());

  const dueToday = tasks.filter((task) => task.status !== 'done' && task.dueDate === todayKey);
  const overdue = tasks.filter((task) => task.status !== 'done' && task.dueDate !== null && task.dueDate < todayKey);
  const scheduledToday: ScheduledItem[] = [
    ...tasks
      .filter((task) => task.scheduledStart && task.scheduledStart.startsWith(todayKey))
      .map((task) => ({
        kind: 'task' as const,
        id: task.id,
        title: task.title,
        timeLabel: buildTimeLabel(task.scheduledStart, task.scheduledEnd),
        task,
      })),
    ...events
      .filter((event) => event.eventDate === todayKey)
      .map((event) => ({
        kind: 'event' as const,
        id: event.id,
        title: event.title,
        timeLabel: event.allDay ? 'All day' : buildEventTimeLabel(event),
        event,
      })),
  ].sort((left, right) => left.timeLabel.localeCompare(right.timeLabel));

  return (
    <div className="today-view" style={{ display: 'grid', gap: 16 }}>
      <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
        <SummaryCard label="Due today" value={dueToday.length.toString()} helper="Tasks that need attention today" />
        <SummaryCard label="Overdue" value={overdue.length.toString()} helper="Open tasks past their date" />
        <SummaryCard label="Scheduled" value={scheduledToday.length.toString()} helper={formatDisplayDate(todayKey)} />
      </div>

      <DailyNoteEditor
        date={todayKey}
        note={todayNote}
        title="Today's note"
        helperText="Use this as your daily log, meeting scratchpad, and decision journal."
        onSave={onSaveNote}
      />

      <div className="summary-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <ListCard<TaskRecord>
          title="Due today"
          emptyText="Nothing due today. Capture or schedule something from the command bar."
          items={dueToday}
          onSelect={onSelectTask}
          renderMeta={(task) => describeDate(task.dueDate)}
        />

        <ListCard<TaskRecord>
          title="Overdue"
          emptyText="No overdue tasks. Nice."
          items={overdue}
          onSelect={onSelectTask}
          renderMeta={(task) => describeDate(task.dueDate)}
        />

        <section
          style={{
            background: 'var(--bg-secondary)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-lg)',
            padding: 16,
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700 }}>Today's schedule</h2>
            <button className="btn btn-ghost btn-sm" onClick={onOpenNotes}>Open notes</button>
          </div>

          {scheduledToday.length === 0 ? (
            <EmptyState message="No scheduled tasks or events yet. Drag a task onto the calendar or create an event." />
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {scheduledToday.map((item) => (
                <button
                  key={`${item.kind}-${item.id}`}
                  className="task-card"
                  style={{ width: '100%', textAlign: 'left', background: 'var(--bg-primary)' }}
                  onClick={() => item.kind === 'task' ? onSelectTask(item.task) : onSelectEvent(item.event)}
                >
                  <div className="task-content">
                    <div className="task-title">{item.title}</div>
                    <div className="task-meta">
                      <span>{item.kind === 'task' ? 'Task' : 'Event'}</span>
                      <span>{item.timeLabel}</span>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function SummaryCard({ label, value, helper }: { label: string; value: string; helper: string }) {
  return (
    <section
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
      }}
    >
      <div style={{ fontSize: 12, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 4 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>{helper}</div>
    </section>
  );
}

function ListCard<T extends { id: string; title: string }>({
  title,
  items,
  onSelect,
  renderMeta,
  emptyText,
}: {
  title: string;
  items: T[];
  onSelect: (item: T) => void;
  renderMeta: (item: T) => string;
  emptyText: string;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-secondary)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 16,
      }}
    >
      <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>{title}</h2>
      {items.length === 0 ? (
        <EmptyState message={emptyText} />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {items.map((item) => (
            <button
              key={item.id}
              className="task-card"
              style={{ width: '100%', textAlign: 'left', background: 'var(--bg-primary)' }}
              onClick={() => onSelect(item)}
            >
              <div className="task-content">
                <div className="task-title">{item.title}</div>
                <div className="task-meta">
                  <span>{renderMeta(item)}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function EmptyState({ message }: { message: string }) {
  return <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{message}</p>;
}

function buildTimeLabel(start: string | null, end: string | null): string {
  if (!start) {
    return 'Scheduled';
  }

  const startLabel = new Date(start).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  if (!end) {
    return startLabel;
  }

  const endLabel = new Date(end).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return `${startLabel} - ${endLabel}`;
}

function buildEventTimeLabel(event: EventRecord): string {
  if (!event.startTime) {
    return 'Scheduled';
  }

  return event.endTime ? `${event.startTime} - ${event.endTime}` : event.startTime;
}
